import React, { useState, useRef, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(ArcElement, Title, Tooltip, Legend);

const CalculateSexPercentage = ({ data }) => {
  const pdfRef = useRef();

  // ✅ Safe fallback
  const safeData = data ?? [];

  // ✅ Always compute years safely
  const years = safeData.length
    ? Object.keys(safeData[0]).filter(
        (key) => key !== "sex_label" && key !== "percentage",
      )
    : [];

  const latestYear = years.length ? years[years.length - 1] : "";

  // ✅ Hooks MUST always run
  const [selectedYear, setSelectedYear] = useState(latestYear);

  const processed = useMemo(() => {
    if (!safeData.length || !selectedYear) return [];

    const values = safeData.map((row) => parseFloat(row[selectedYear]) || 0);

    const total = values.reduce((sum, val) => sum + val, 0);

    return safeData.map((row, index) => ({
      label: row.sex_label,
      value: values[index],
      percentage: total > 0 ? ((values[index] / total) * 100).toFixed(2) : 0,
    }));
  }, [safeData, selectedYear]);

  // Now it is SAFE to conditionally render
  if (!safeData.length) {
    return <p>No data available</p>;
  }

  const totalSelected = processed.reduce((sum, row) => sum + row.value, 0);

  const dominantSegment =
    processed.length > 0
      ? processed.reduce((max, row) => (row.value > max.value ? row : max))
      : { label: "-", percentage: 0 };

  const chartData = {
    labels: processed.map((row) => row.label),
    datasets: [
      {
        data: processed.map((row) => row.value),
        backgroundColor: ["#EC4899", "#0ea5e9", "#014CB2"],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `📊 Employment Distribution by Sex (${selectedYear})`,
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const pct = processed[context.dataIndex]?.percentage ?? 0;
            return `${context.label}: ${context.parsed.toLocaleString()} (${pct}%)`;
          },
        },
      },
    },
  };

  // PDF logic remains same...

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Distribution by Sex
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Selected Year</p>
            <p className="text-xl font-bold text-blue-800">{selectedYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment
            </p>
            <p className="text-xl font-bold text-green-800">
              {totalSelected.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Dominant Segment
            </p>
            <p className="text-xl font-bold text-yellow-800">
              {dominantSegment.label} ({dominantSegment.percentage}%)
            </p>
          </div>
        </div>

        {/* Year Selector */}
        <div className="text-center mb-6">
          <label className="mr-2 font-medium">Select Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border px-3 py-1 rounded-md shadow-sm"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Sex</th>
                  <th className="p-2">Count</th>
                  <th className="p-2">Share (%)</th>
                </tr>
              </thead>
              <tbody>
                {processed.map((row) => (
                  <tr key={row.label} className="hover:bg-gray-100">
                    <td className="p-2">{row.label}</td>
                    <td className="p-2">{row.value.toLocaleString()}</td>
                    <td className="p-2">{row.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-96">
            <Doughnut data={chartData} options={options} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CalculateSexPercentage;
